// src/pages/DuaDhikr.tsx
import React, { useState, useEffect } from 'react';
import DonationSidebar from '../components/DonationSidebar';

const DuaDhikr: React.FC = () => {
  const [activeSection, setActiveSection] = useState<'top5' | 'daily' | 'forgiveness' | 'protection' | 'emotional'>('top5');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const top5Duas = [
    {
      id: 1,
      title: "Sayyidul Istighfar (The Master of Forgiveness)",
      arabic: "اَللّٰهُمَّ أَنْتَ رَبِّي، لَا إِلٰهَ إِلَّا أَنْتَ، خَلَقْتَنِي وَأَنَا عَبْدُكَ، وَأَنَا عَلَى عَهْدِكَ وَوَعْدِكَ مَا اسْتَطَعْتُ، أَعُوذُ بِكَ مِنْ شَرِّ مَا صَنَعْتُ، أَبُوءُ لَكَ بِنِعْمَتِكَ عَلَيَّ، وَأَبُوءُ بِذَنْبِي، فَاغْفِرْ لِي، فَإِنَّهُ لَا يَغْفِرُ الذُّنُوبَ إِلَّا أَنْتَ",
      transliteration: "Allahumma anta Rabbi, la ilaha illa Anta, khalaqtani wa ana 'abduka, wa ana 'ala 'ahdika wa wa'dika ma istata't, a'udhu bika min sharri ma sana't, abu'u laka bini'matika 'alayya, wa abu'u bidhanbi, faghfir li fa innahu la yaghfiru adh-dhunuba illa Anta",
      translation: "O Allah, You are my Lord. None has the right to be worshipped but You. You created me and I am Your servant. I keep Your covenant and promise as much as I can. I seek refuge with You from the evil of what I have done. I acknowledge before You all the blessings You have bestowed upon me, and I confess to You all my sins. So forgive me, for none forgives sins except You.",
      reference: "Bukhari 6306",
      reason: "The Prophet ﷺ said: 'Whoever says this during the day with firm belief and dies that day, will enter Paradise.'"
    },
    {
      id: 2,
      title: "Prophet Yunus' Dua (In Hardship, Depression, or Guilt)",
      arabic: "لَا إِلٰهَ إِلَّا أَنْتَ سُبْحَانَكَ إِنِّي كُنْتُ مِنَ الظَّالِمِينَ",
      transliteration: "La ilaha illa Anta Subhanaka inni kuntu minaz-zalimin",
      translation: "None has the right to be worshipped except You. Glory be to You. Indeed, I have been among the wrongdoers.",
      reference: "Qur'an 21:87",
      reason: "Mentioned in context of Prophet Yunus in the belly of the whale. The Prophet ﷺ said: 'No Muslim supplicates with this except that Allah answers him.' (Tirmidhi 3505)"
    },
    {
      id: 3,
      title: "Comprehensive Dua for Goodness & Protection",
      arabic: "رَبَّنَا آتِنَا فِي الدُّنْيَا حَسَنَةً وَفِي الْآخِرَةِ حَسَنَةً وَقِنَا عَذَابَ النَّارِ",
      transliteration: "Rabbana atina fid-dunya hasanatan wa fil-akhirati hasanatan waqina 'adhaban-nar",
      translation: "Our Lord! Grant us good in this world and good in the Hereafter, and protect us from the punishment of the Fire.",
      reference: "Qur'an 2:201",
      reason: "The Prophet ﷺ recited this more than any other dua. It perfectly balances worldly and spiritual needs."
    },
    {
      id: 4,
      title: "Dua for Knowledge and Growth",
      arabic: "رَبِّ زِدْنِي عِلْمًا",
      transliteration: "Rabbi zidni 'ilma",
      translation: "My Lord, increase me in knowledge.",
      reference: "Qur'an 20:114",
      reason: "The only dua in the Qur'an directly commanding the Prophet ﷺ to ask for more of something, and that is knowledge."
    },
    {
      id: 5,
      title: "Astaghfirullah (Strongest Forgiveness Formula)",
      arabic: "أَسْتَغْفِرُ اللّٰهَ الْعَظِيمَ الَّذِي لَا إِلٰهَ إِلَّا هُوَ الْحَيُّ الْقَيُّومُ وَأَتُوبُ إِلَيْهِ",
      transliteration: "Astaghfirullah al-'Azim alladhi la ilaha illa Huwa al-Hayyul Qayyum wa atubu ilayh",
      translation: "I seek forgiveness from Allah, the Ever-Living, the Sustainer, and I repent to Him.",
      reference: "Abu Dawud 1517",
      reason: "Prophet ﷺ said: 'Whoever says this, his sins will be forgiven even if he fled from battle.'"
    }
  ];

  const dailyLifeDuas = [
    { id: 1, purpose: "Morning Dhikr", arabic: "أَصْبَحْنَا وَأَصْبَحَ الْمُلْكُ لِلّٰهِ", transliteration: "Asbahna wa asbahal-mulku lillah", translation: "We have reached the morning and all sovereignty belongs to Allah.", reference: "Muslim" },
    { id: 2, purpose: "Evening Dhikr", arabic: "أَمْسَيْنَا وَأَمْسَى الْمُلْكُ لِلّٰهِ", transliteration: "Amsayna wa amsal-mulku lillah", translation: "We have reached the evening and all sovereignty belongs to Allah.", reference: "Muslim" },
    { id: 3, purpose: "Before Eating", arabic: "بِسْمِ اللّٰهِ", transliteration: "Bismillah", translation: "In the name of Allah.", reference: "Bukhari" },
    { id: 4, purpose: "After Eating", arabic: "الْحَمْدُ لِلّٰهِ الَّذِي أَطْعَمَنَا وَسَقَانَا", transliteration: "Alhamdulillahil-ladhi at'amana wa saqana", translation: "All praise is for Allah who fed us and gave us drink.", reference: "Abu Dawud" },
    { id: 5, purpose: "Before Sleeping", arabic: "بِاسْمِكَ اللّٰهُمَّ أَحْيَا وَبِاسْمِكَ أَمُوتُ", transliteration: "Bismika Allahumma ahya wa bismika amut", translation: "In Your name I live, and in Your name I die.", reference: "Bukhari" },
    { id: 6, purpose: "Waking Up", arabic: "الْحَمْدُ لِلّٰهِ الَّذِي أَحْيَانَا بَعْدَ مَا أَمَاتَنَا", transliteration: "Alhamdulillahi alladhi ahyana ba'da ma amatana", translation: "Praise be to Allah who gave us life after causing us to die.", reference: "Bukhari" },
    { id: 7, purpose: "Entering Home", arabic: "بِسْمِ اللّٰهِ وَلَجْنَا وَبِسْمِ اللّٰهِ خَرَجْنَا", transliteration: "Bismillah walajna wa bismillah kharajna", translation: "In the name of Allah we enter and leave, and upon Him we rely.", reference: "Abu Dawud" },
    { id: 8, purpose: "Leaving Home", arabic: "بِسْمِ اللّٰهِ، تَوَكَّلْتُ عَلَى اللّٰهِ", transliteration: "Bismillah, tawakkaltu 'ala Allah", translation: "In the name of Allah, I place my trust in Allah.", reference: "Abu Dawud" },
    { id: 9, purpose: "Travel", arabic: "سُبْحَانَ الَّذِي سَخَّرَ لَنَا هَٰذَا", transliteration: "Subhanalladhi sakhkhara lana hadha", translation: "Glory be to Him who subjected this to us.", reference: "Qur'an 43:13-14" },
    { id: 10, purpose: "For Good Provision", arabic: "اللّٰهُمَّ اكْفِنِي بِحَلَالِكَ عَنْ حَرَامِكَ", transliteration: "Allahumma akfini bihalalika 'an haramika", translation: "O Allah, suffice me with what is lawful over what is unlawful.", reference: "Tirmidhi 3563" }
  ];

  const forgivenessDuas = [
    { id: 11, purpose: "Forgiveness (Adam's Dua)", arabic: "رَبَّنَا ظَلَمْنَا أَنفُسَنَا", transliteration: "Rabbana zalamna anfusana", translation: "Our Lord, we have wronged ourselves; if You forgive us not, we'll be among the losers.", reference: "Qur'an 7:23" },
    { id: 12, purpose: "Sayyidul Istighfar", arabic: "اللّٰهُمَّ أَنْتَ رَبِّي لَا إِلٰهَ إِلَّا أَنْتَ...", transliteration: "Allahumma anta Rabbi la ilaha illa Anta...", translation: "O Allah, You are my Lord; there is no god but You. You created me and I am Your servant...", reference: "Bukhari 6306" },
    { id: 13, purpose: "Astaghfirullah (Strongest Repentance)", arabic: "أَسْتَغْفِرُ اللّٰهَ الْعَظِيمَ الَّذِي لَا إِلٰهَ إِلَّا هُوَ الْحَيُّ الْقَيُّومُ وَأَتُوبُ إِلَيْهِ", transliteration: "Astaghfirullah al-'Azim alladhi la ilaha illa Huwa al-Hayyul Qayyum wa atubu ilayh", translation: "I seek forgiveness from Allah, the Ever-Living, the Sustainer, and I repent to Him.", reference: "Abu Dawud 1517" },
    { id: 14, purpose: "For General Forgiveness", arabic: "رَبِّ اغْفِرْ وَارْحَمْ وَأَنْتَ خَيْرُ الرَّاحِمِينَ", transliteration: "Rabbi ighfir warham wa anta khayrur rahimin", translation: "My Lord, forgive and have mercy, and You are the best of the merciful.", reference: "Qur'an 23:118" },
    { id: 15, purpose: "For Forgiveness of All Believers", arabic: "رَبَّنَا اغْفِرْ لَنَا وَلِإِخْوَانِنَا الَّذِينَ سَبَقُونَا بِالْإِيمَانِ", transliteration: "Rabbana ighfir lana wa li ikhwanina alladhina sabaquna bil iman", translation: "Our Lord, forgive us and our brothers who preceded us in faith.", reference: "Qur'an 59:10" },
    { id: 16, purpose: "When Sin Feels Heavy", arabic: "اللّٰهُمَّ مَغْفِرَتَكَ أَوْسَعُ مِنْ ذُنُوبِي", transliteration: "Allahumma maghfirataka awsa'u min dhunubi", translation: "O Allah, Your forgiveness is greater than my sins.", reference: "Authentic narration (Ibn Majah)" },
    { id: 17, purpose: "For a Clean Heart", arabic: "رَبَّنَا لَا تُزِغْ قُلُوبَنَا بَعْدَ إِذْ هَدَيْتَنَا", transliteration: "Rabbana la tuzigh qulubana ba'da idh hadaytana", translation: "Our Lord, do not let our hearts deviate after You have guided us.", reference: "Qur'an 3:8" },
    { id: 18, purpose: "For Mercy", arabic: "رَبِّ اغْفِرْ لِي وَلِوَالِدَيَّ وَلِلْمُؤْمِنِينَ", transliteration: "Rabbi ighfir li wa li walidayya wa lil-mu'mineen", translation: "My Lord, forgive me, my parents, and all believers.", reference: "Qur'an 14:41" },
    { id: 19, purpose: "After Sin or Mistake", arabic: "لَا إِلٰهَ إِلَّا أَنْتَ سُبْحَانَكَ إِنِّي كُنْتُ مِنَ الظَّالِمِينَ", transliteration: "La ilaha illa Anta Subhanaka inni kuntu minaz-zalimin", translation: "None has the right to be worshipped but You. Glory be to You, indeed I was among the wrongdoers.", reference: "Qur'an 21:87" },
    { id: 20, purpose: "When Feeling Shame or Regret", arabic: "رَبِّ تَقَبَّلْ تَوْبَتِي وَاغْسِلْ حَوْبَتِي", transliteration: "Rabbi taqabbal tawbati waghsi ḥaubati", translation: "My Lord, accept my repentance and cleanse me of my faults.", reference: "Ibn Majah 3815" }
  ];

  const protectionDuas = [
    { id: 21, purpose: "For Protection", arabic: "أَعُوذُ بِكَلِمَاتِ اللّٰهِ التَّامَّاتِ مِن شَرِّ مَا خَلَقَ", transliteration: "A'udhu bi kalimatillahit-tammati min sharri ma khalaq", translation: "I seek refuge in the perfect words of Allah from the evil of what He has created.", reference: "Muslim" },
    { id: 22, purpose: "From Evil Eye", arabic: "أَعُوذُ بِكَلِمَاتِ اللّٰهِ التَّامَّةِ مِنْ كُلِّ شَيْطَانٍ وَهَامَّةٍ وَمِنْ كُلِّ عَيْنٍ لَامَّةٍ", transliteration: "A'udhu bikalimatillahi tammati min kulli shaytanin wa hammatin wa min kulli 'aynin lammah", translation: "I seek refuge in the perfect words of Allah from every devil, creature, and harmful eye.", reference: "Bukhari 3371" },
    { id: 23, purpose: "Against Hardship", arabic: "حَسْبُنَا اللّٰهُ وَنِعْمَ الْوَكِيلُ", transliteration: "Hasbunallahu wa ni'mal wakeel", translation: "Allah is sufficient for us, and He is the best disposer of affairs.", reference: "Qur'an 3:173" },
    { id: 24, purpose: "Against Fear or Worry", arabic: "لَا حَوْلَ وَلَا قُوَّةَ إِلَّا بِاللّٰهِ", transliteration: "La hawla wa la quwwata illa billah", translation: "There is no power nor strength except with Allah.", reference: "Muslim" },
    { id: 25, purpose: "For Strength", arabic: "اللَّهُمَّ إِنِّي أَعُوذُ بِكَ مِنَ الْعَجْزِ وَالْكَسَلِ", transliteration: "Allahumma inni a'udhu bika minal-'ajzi wal-kasal", translation: "O Allah, I seek refuge in You from weakness and laziness.", reference: "Bukhari 6369" },
    { id: 26, purpose: "When in Fear", arabic: "رَبِّ إِنِّي مَغْلُوبٌ فَانْتَصِرْ", transliteration: "Rabbi inni maghlubun fantasir", translation: "My Lord, I am overpowered, so help me.", reference: "Qur'an 54:10" },
    { id: 27, purpose: "For Inner Calm", arabic: "أَلَا بِذِكْرِ اللّٰهِ تَطْمَئِنُّ الْقُلُوبُ", transliteration: "Ala bidhikrillahi tatma'innul qulub", translation: "Indeed, in the remembrance of Allah do hearts find rest.", reference: "Qur'an 13:28" },
    { id: 28, purpose: "Protection from Harm", arabic: "اللّٰهُمَّ احْفَظْنِي مِنْ بَيْنِ يَدَيَّ وَمِنْ خَلْفِي...", transliteration: "Allahumma ihfazni min bayni yadayya wa min khalfi...", translation: "O Allah, protect me from in front of me, behind me, my right, my left, and above me.", reference: "Abu Dawud 5074" },
    { id: 29, purpose: "Protection from Shaytan", arabic: "أَعُوذُ بِاللّٰهِ مِنَ الشَّيْطَانِ الرَّجِيمِ", transliteration: "A'udhu billahi min ash-shaytanir-rajim", translation: "I seek refuge in Allah from Satan the accursed.", reference: "Qur'an 16:98" },
    { id: 30, purpose: "For Guidance in Life", arabic: "رَبِّ يَسِّرْ وَلَا تُعَسِّرْ", transliteration: "Rabbi yassir wa la tu'assir", translation: "My Lord, make it easy and do not make it difficult.", reference: "Authentic Du'a (Ibn Hibban)" }
  ];

  const emotionalDuas = [
    { id: 31, purpose: "When Sad or Depressed", arabic: "اللَّهُمَّ إِنِّي أَعُوذُ بِكَ مِنَ الْهَمِّ وَالْحُزْنِ", transliteration: "Allahumma inni a'udhu bika minal-hammi wal-huzn", translation: "O Allah, I seek refuge in You from anxiety and sorrow.", reference: "Bukhari 6369" },
    { id: 32, purpose: "When Feeling Weak", arabic: "اللَّهُمَّ إِنِّي فَقِيرٌ إِلَيْكَ", transliteration: "Allahumma inni faqirun ilayk", translation: "O Allah, I am in need of You.", reference: "Qur'an 28:24" },
    { id: 33, purpose: "When Seeking Patience", arabic: "رَبِّ أَفْرِغْ عَلَيَّ صَبْرًا", transliteration: "Rabbi afrigh 'alayya sabra", translation: "My Lord, pour upon me patience.", reference: "Qur'an 7:126" },
    { id: 34, purpose: "When Lonely or Unloved", arabic: "يَا حَيُّ يَا قَيُّومُ بِرَحْمَتِكَ نَسْتَغِيثُ", transliteration: "Ya Hayyu Ya Qayyumu birahmatika nastagheeth", translation: "O Ever-Living, O Sustainer, by Your mercy we seek help.", reference: "Tirmidhi 3524" },
    { id: 35, purpose: "When Grateful", arabic: "الْحَمْدُ لِلّٰهِ عَلَى كُلِّ حَالٍ", transliteration: "Alhamdulillahi 'ala kulli hal", translation: "All praise is for Allah in every situation.", reference: "Ibn Majah 3803" },
    { id: 36, purpose: "When Seeking Knowledge", arabic: "رَبِّ زِدْنِي عِلْمًا", transliteration: "Rabbi zidni 'ilma", translation: "My Lord, increase me in knowledge.", reference: "Qur'an 20:114" },
    { id: 37, purpose: "When Seeking Peace", arabic: "سُبْحَانَ اللّٰهِ وَبِحَمْدِهِ", transliteration: "Subhanallahi wa bihamdih", translation: "Glory be to Allah and praise be to Him.", reference: "Muslim 2691" },
    { id: 38, purpose: "When Grateful & Content", arabic: "الْحَمْدُ لِلّٰهِ الَّذِي أَنْعَمَ عَلَيَّ", transliteration: "Alhamdulillahi alladhi an'ama 'alayya", translation: "Praise be to Allah who has blessed me.", reference: "Qur'an 27:19" },
    { id: 39, purpose: "When Seeking Light in Heart", arabic: "رَبَّنَا أَتْمِمْ لَنَا نُورَنَا", transliteration: "Rabbana atmim lana nurana", translation: "Our Lord, perfect for us our light and forgive us.", reference: "Qur'an 66:8" },
    { id: 40, purpose: "When Hopeless or Desperate", arabic: "لَا تَقْنَطُوا مِن رَّحْمَةِ اللّٰهِ", transliteration: "La taqnatu min rahmatillah", translation: "Do not despair of the mercy of Allah.", reference: "Qur'an 39:53" }
  ];

  const renderDuas = (duas: any[]) => {
    return (
      <div className="grid-2">
        {duas.map((dua) => (
          <div key={dua.id} className="card">
            <h3 style={{ marginBottom: '16px', color: 'var(--text-primary)' }}>
              {dua.purpose || dua.title}
            </h3>
            <div className="arabic" style={{
              fontSize: '20px',
              lineHeight: '1.8',
              marginBottom: '12px',
              textAlign: 'right',
              direction: 'rtl'
            }}>
              {dua.arabic}
            </div>
            <div style={{
              color: 'var(--accent-primary)',
              fontSize: '14px',
              fontStyle: 'italic',
              marginBottom: '8px'
            }}>
              {dua.transliteration}
            </div>
            <div style={{
              color: 'var(--text-secondary)',
              fontSize: '14px',
              marginBottom: '8px',
              lineHeight: '1.6'
            }}>
              {dua.translation}
            </div>
            {dua.reason && (
              <div style={{
                color: 'var(--accent-primary)',
                fontSize: '13px',
                marginBottom: '8px',
                fontStyle: 'italic',
                backgroundColor: 'var(--bg-secondary)',
                padding: '8px 12px',
                borderRadius: '6px'
              }}>
                💡 {dua.reason}
              </div>
            )}
            <div style={{
              color: 'var(--text-secondary)',
              fontSize: '12px',
              opacity: '0.7'
            }}>
              {dua.reference}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="section-padding">
      <div className="container">
        <div className="section-title">
          <h1>Dua & Dhikr</h1>
          <p>Authentic supplications from Quran and Sunnah for every occasion</p>
        </div>

        {/* Navigation Tabs */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '40px',
          borderBottom: '1px solid var(--border)',
          flexWrap: 'wrap',
          gap: '8px'
        }}>
          <button
            onClick={() => setActiveSection('top5')}
            style={{
              padding: '12px 24px',
              border: 'none',
              backgroundColor: activeSection === 'top5' ? 'var(--accent-primary)' : 'transparent',
              color: activeSection === 'top5' ? 'var(--bg-primary)' : 'var(--text-secondary)',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              borderBottom: activeSection === 'top5' ? '2px solid var(--accent-primary)' : 'none',
              borderRadius: '8px 8px 0 0'
            }}
          >
            🌟 Top 5 Powerful Duas
          </button>
          <button
            onClick={() => setActiveSection('daily')}
            style={{
              padding: '12px 24px',
              border: 'none',
              backgroundColor: activeSection === 'daily' ? 'var(--accent-primary)' : 'transparent',
              color: activeSection === 'daily' ? 'var(--bg-primary)' : 'var(--text-secondary)',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              borderBottom: activeSection === 'daily' ? '2px solid var(--accent-primary)' : 'none',
              borderRadius: '8px 8px 0 0'
            }}
          >
            🌅 Daily Life Duas
          </button>
          <button
            onClick={() => setActiveSection('forgiveness')}
            style={{
              padding: '12px 24px',
              border: 'none',
              backgroundColor: activeSection === 'forgiveness' ? 'var(--accent-primary)' : 'transparent',
              color: activeSection === 'forgiveness' ? 'var(--bg-primary)' : 'var(--text-secondary)',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              borderBottom: activeSection === 'forgiveness' ? '2px solid var(--accent-primary)' : 'none',
              borderRadius: '8px 8px 0 0'
            }}
          >
            🕊️ Forgiveness & Repentance
          </button>
          <button
            onClick={() => setActiveSection('protection')}
            style={{
              padding: '12px 24px',
              border: 'none',
              backgroundColor: activeSection === 'protection' ? 'var(--accent-primary)' : 'transparent',
              color: activeSection === 'protection' ? 'var(--bg-primary)' : 'var(--text-secondary)',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              borderBottom: activeSection === 'protection' ? '2px solid var(--accent-primary)' : 'none',
              borderRadius: '8px 8px 0 0'
            }}
          >
            🛡️ Protection & Strength
          </button>
          <button
            onClick={() => setActiveSection('emotional')}
            style={{
              padding: '12px 24px',
              border: 'none',
              backgroundColor: activeSection === 'emotional' ? 'var(--accent-primary)' : 'transparent',
              color: activeSection === 'emotional' ? 'var(--bg-primary)' : 'var(--text-secondary)',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              borderBottom: activeSection === 'emotional' ? '2px solid var(--accent-primary)' : 'none',
              borderRadius: '8px 8px 0 0'
            }}
          >
            💖 Emotional Healing
          </button>
        </div>

        {/* Content Sections */}
        {activeSection === 'top5' && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <h2 style={{ color: 'var(--text-primary)', marginBottom: '16px' }}>🌟 Top 5 Most Powerful Duas</h2>
              <p style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
                These are the most comprehensive and powerful supplications from the Quran and Sunnah that cover all aspects of life.
              </p>
            </div>
            {renderDuas(top5Duas)}
          </div>
        )}

        {activeSection === 'daily' && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <h2 style={{ color: 'var(--text-primary)', marginBottom: '16px' }}>🌅 Daily Life Duas</h2>
              <p style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
                Essential supplications for everyday activities and routines.
              </p>
            </div>
            {renderDuas(dailyLifeDuas)}
          </div>
        )}

        {activeSection === 'forgiveness' && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <h2 style={{ color: 'var(--text-primary)', marginBottom: '16px' }}>🕊️ Forgiveness & Repentance</h2>
              <p style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
                Powerful duas for seeking forgiveness, mercy, and spiritual purification.
              </p>
            </div>
            {renderDuas(forgivenessDuas)}
          </div>
        )}

        {activeSection === 'protection' && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <h2 style={{ color: 'var(--text-primary)', marginBottom: '16px' }}>🛡️ Protection & Strength</h2>
              <p style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
                Duas for protection from harm, evil, and seeking strength from Allah.
              </p>
            </div>
            {renderDuas(protectionDuas)}
          </div>
        )}

        {activeSection === 'emotional' && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <h2 style={{ color: 'var(--text-primary)', marginBottom: '16px' }}>💖 Emotional & Spiritual Healing</h2>
              <p style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
                Healing supplications for emotional well-being and spiritual comfort.
              </p>
            </div>
            {renderDuas(emotionalDuas)}
          </div>
        )}

        {/* Donation Sidebar */}
        <DonationSidebar />
      </div>
    </div>
  );
};

export default DuaDhikr;